import { injectable, inject } from 'inversify';

import { History } from '@vk-hr-tek/core/history';

import { RedirectPage } from '../../types';

const ONE_SECOND_IN_MILISECONDS = 1000;
const SECONDS_COUNT = 60;
const MINUTES_COUNT = 10;

const TEN_MINS = ONE_SECOND_IN_MILISECONDS * SECONDS_COUNT * MINUTES_COUNT;

@injectable()
export class AuthRouterService {
  private readonly key = 'redirectPage';

  private readonly savedPageLifetime = TEN_MINS;

  constructor(
    @inject(History) private history: History,
    @inject(RedirectPage)
    private readonly redirectPageService: RedirectPage,
  ) {}

  redirectToHome() {
    this.history.push('/');
  }

  redirectToLogin() {
    this.saveRedirectPage();

    this.history.push({
      pathname: '/auth/login',
      search: '',
    });
  }

  redirectToAliLogin() {
    this.history.push({
      pathname: '/auth/ali',
      search: '',
    });
  }

  redirectToCandidateLogin() {
    this.history.push({
      pathname: '/auth/candidate',
      search: '',
    });
  }

  redirectToQr() {
    this.history.push({
      pathname: '/auth/qr',
      search: '',
    });
  }

  redirectToAuthLogin(id: string) {
    this.history.push(`/auth/${id}/login`);
  }

  redirectToAuthPass(code: string) {
    this.history.push(`/auth/pass?code=${code}`);
  }

  redirectToAuthList() {
    this.history.push('/auth/auth-list');
  }

  redirectToRegister(authInstanceId?: string) {
    this.history.push({
      pathname: authInstanceId
        ? `/auth/${authInstanceId}/register`
        : '/auth/register',
      search: '',
    });
  }

  redirectToPasswordChange(authInstanceId?: string) {
    this.history.push({
      pathname: authInstanceId
        ? `/auth/${authInstanceId}/change-password`
        : '/auth/change-password',
      search: '',
    });
  }

  redirectToPhoneChange(authInstanceId?: string) {
    this.history.push({
      pathname: authInstanceId
        ? `/auth/${authInstanceId}/register`
        : '/auth/register',
      search: '?target=phone_change',
    });
  }

  redirectToSendInvite() {
    this.history.push({
      pathname: '/auth/send-invite',
      search: '',
    });
  }

  redirectToSendInviteForPhone() {
    this.history.push({
      pathname: '/auth/send-invite',
      search: '?target=phone_change',
    });
  }

  redirectToSaved() {
    try {
      const { pathname, search, hash, timestamp } = JSON.parse(
        this.redirectPageService.get(),
      );

      if (
        typeof pathname !== 'string' ||
        typeof search !== 'string' ||
        typeof hash !== 'string' ||
        typeof timestamp !== 'number'
      ) {
        throw new Error('Invalid saved page data');
      }

      if (Date.now() - timestamp > this.savedPageLifetime) {
        throw new Error('Redirect page expired.');
      }

      this.redirectPageService.delete();
      this.history.push({ pathname, search, hash });
    } catch (err) {
      this.redirectPageService.delete();
      this.redirectToHome();
    }
  }

  saveRedirectPage(): void {
    if (!this.history.location.pathname.startsWith('/auth/')) {
      this.redirectPageService.save(
        JSON.stringify({
          pathname: this.history.location.pathname,
          search: this.history.location.search || '',
          hash: this.history.location.hash || '',
          timestamp: Date.now(),
        }),
      );
    }
  }

  isVKUser(): boolean {
    const redirectPage = this.redirectPageService.get();

    try {
      return !!(
        redirectPage &&
        JSON.parse(redirectPage).search &&
        JSON.parse(redirectPage).search.match('vk=1')
      );
    } catch (err) {
      return false;
    }
  }
}
